import React from "react";
import {
  Alert,
  Card,
  Col,
  InputNumber,
  Radio,
  Row,
  Skeleton,
  Statistic,
  Typography,
  Button,
  Tag,
} from "antd";
import {
  ArrowRightOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { CorralParticipant, CorralStrategy } from "../types/corral.types";
import DistanceFilterCards from "../../bib/components/DistanceFilterCards";

const { Text, Title } = Typography;

interface CorralEstimationProps {
  // Event / distances
  distances: string[];
  selectedDistance: string | undefined;
  onDistanceSelect: (d: string | undefined) => void;
  savedRangesForFilter: Record<string, any>;
  distanceTotals: Record<string, number>;
  eventLoading: boolean;

  // Participants
  participants: CorralParticipant[];
  loadingParticipants: boolean;
  total: number;

  // Strategy
  strategy: CorralStrategy;
  onStrategyChange: (s: CorralStrategy) => void;
  fixedSize: number;
  onFixedSizeChange: (n: number) => void;

  // Navigation
  onNext: () => void;
}

const CorralEstimation: React.FC<CorralEstimationProps> = ({
  distances,
  selectedDistance,
  onDistanceSelect,
  savedRangesForFilter,
  distanceTotals,
  eventLoading,
  participants,
  loadingParticipants,
  total,
  strategy,
  onStrategyChange,
  fixedSize,
  onFixedSizeChange,
  onNext,
}) => {
  const withBib = participants.filter((p) => p.bib_number);
  const noBib = participants.filter((p) => !p.bib_number);
  const timedCount = participants.filter(
    (p) => p.bib_number && p.expected_finish_time !== undefined,
  ).length;

  const hasTimingData = timedCount > 0;
  const estimatedCorrals =
    strategy === "fixed_size" && fixedSize > 0
      ? Math.ceil(withBib.length / fixedSize)
      : 4;

  const canProceed = !!selectedDistance && withBib.length > 0;

  return (
    <div>
      {/* ── Distance Selection ────────────────────────────────────────────── */}
      <Card
        bordered={false}
        style={{ marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
      >
        {eventLoading ? (
          <Skeleton active paragraph={{ rows: 1 }} />
        ) : (
          <DistanceFilterCards
            distances={distances}
            selected={selectedDistance}
            onSelect={onDistanceSelect}
            savedRanges={savedRangesForFilter}
            totalByDistance={distanceTotals}
          />
        )}
      </Card>

      {/* ── Summary Cards (only when distance selected) ───────────────────── */}
      {selectedDistance && (
        <>
          {loadingParticipants ? (
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              {[1, 2, 3].map((i) => (
                <Col key={i} xs={24} sm={8}>
                  <Card bordered={false} style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
                    <Skeleton active paragraph={{ rows: 1 }} />
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
                  <Statistic
                    title="Total Registered"
                    value={total}
                    prefix={<TeamOutlined style={{ color: "#2563eb" }} />}
                    valueStyle={{ color: "#1d4ed8" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
                  <Statistic
                    title="BIB Assigned"
                    value={withBib.length}
                    suffix={
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        / {total}
                      </Text>
                    }
                    valueStyle={{ color: "#16a34a" }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card bordered={false} style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
                  <Statistic
                    title="Timed Runners"
                    value={timedCount}
                    prefix={<ClockCircleOutlined style={{ color: "#7c3aed" }} />}
                    valueStyle={{ color: "#7c3aed" }}
                    suffix={
                      !hasTimingData ? (
                        <Tag color="default" style={{ fontSize: 10 }}>
                          None
                        </Tag>
                      ) : undefined
                    }
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* ── No BIB Warning ─────────────────────────────────────────────── */}
          {noBib.length > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${noBib.length} participant${noBib.length !== 1 ? "s" : ""} have no BIB assigned`}
              description="These runners will be placed in the Default corral. Complete BIB assignment for best results."
              style={{ marginBottom: 20 }}
            />
          )}

          {/* ── Strategy Selection ─────────────────────────────────────────── */}
          <Card
            bordered={false}
            style={{ marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
          >
            <Title level={5} style={{ marginBottom: 16, fontWeight: 600 }}>
              Corral Strategy
            </Title>

            <Radio.Group
              value={strategy}
              onChange={(e) => onStrategyChange(e.target.value)}
              style={{ width: "100%" }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Fixed size option */}
                <div
                  style={{
                    border: `1.5px solid ${strategy === "fixed_size" ? "#2563eb" : "#e2e8f0"}`,
                    borderRadius: 12,
                    padding: "16px 20px",
                    backgroundColor: strategy === "fixed_size" ? "#eff6ff" : "#fff",
                    transition: "all 0.15s",
                    cursor: "pointer",
                  }}
                  onClick={() => onStrategyChange("fixed_size")}
                >
                  <Radio value="fixed_size">
                    <Text strong>Assign corrals using fixed group size</Text>
                  </Radio>
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: 12, marginTop: 4, marginLeft: 24 }}
                  >
                    Group runners sequentially by BIB number into equal-sized corrals.
                  </Text>
                  {strategy === "fixed_size" && (
                    <div
                      style={{
                        marginTop: 12,
                        marginLeft: 24,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <Text style={{ fontSize: 13 }}>Participants per corral:</Text>
                      <InputNumber
                        min={10}
                        max={withBib.length || 1000}
                        value={fixedSize}
                        onChange={(v) => v && onFixedSizeChange(v)}
                        style={{ width: 100 }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {withBib.length > 0 && fixedSize > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          → ~{estimatedCorrals} corral{estimatedCorrals !== 1 ? "s" : ""}
                        </Text>
                      )}
                    </div>
                  )}
                </div>

                {/* Time-based option */}
                <div
                  style={{
                    border: `1.5px solid ${strategy === "time_based" ? "#7c3aed" : "#e2e8f0"}`,
                    borderRadius: 12,
                    padding: "16px 20px",
                    backgroundColor: strategy === "time_based" ? "#faf5ff" : "#fff",
                    transition: "all 0.15s",
                    cursor: "pointer",
                  }}
                  onClick={() => onStrategyChange("time_based")}
                >
                  <Radio value="time_based">
                    <Text strong>
                      Assign corrals based on runners' finish times
                    </Text>
                    {!hasTimingData && (
                      <Tag color="orange" style={{ marginLeft: 8, fontSize: 10 }}>
                        No timing data
                      </Tag>
                    )}
                  </Radio>
                  <Text
                    type="secondary"
                    style={{ display: "block", fontSize: 12, marginTop: 4, marginLeft: 24 }}
                  >
                    {hasTimingData
                      ? `${timedCount} timed runners found. Groups: Elite (<45 min), Advanced (45–60), Intermediate (60–90), Open (90+).`
                      : "No timing data available. Will fall back to 4 equal groups sorted by BIB."}
                  </Text>
                </div>
              </div>
            </Radio.Group>
          </Card>

          {/* ── Next Button ─────────────────────────────────────────────────── */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              disabled={!canProceed}
              onClick={onNext}
            >
              Next: Review Assignment
            </Button>
          </div>

          {!canProceed && selectedDistance && withBib.length === 0 && (
            <Text
              type="secondary"
              style={{ display: "block", textAlign: "right", marginTop: 8, fontSize: 12 }}
            >
              No participants with BIB found. Assign BIBs before proceeding.
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export default CorralEstimation;
